/**
 * region-undo.ts - Item 28 (v0.2.4)
 *
 * Bounded snapshot undo stack for region operations. Each destructive region
 * operation (Merge, Delete, Fill Islands, Reflood, Remap) pushes a snapshot
 * before applying the change. Cmd/Ctrl+Z pops the latest snapshot and restores
 * the region state.
 *
 * Phase 0 memo v0.2.5.0-memory-desktop-memo.md §2: undo stores only the
 * mutable render state needed to restore region operations. The raw
 * originalLabelmapImage is a derived per-upload asset and intentionally stays
 * live in DemoState instead of being copied into every snapshot.
 *
 * Portability: this file is under apps/demo/src/util/ and is covered by the
 * check:portable directory-scope rule. No document/window/fetch/Node globals
 * are used.
 */

import type { Asset, RegionConfig, RegionEntry, RegionStack } from '../state';
import type { DemoState } from '../state';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Indexed representation of labelMap for undo snapshots.
 *
 * The render labelMap stores the label index in the R channel. Undo only needs
 * that one-byte index per pixel plus enough palette data to reconstruct an
 * ImageData object for the existing render pipeline.
 */
export type IndexedLabelMapSnapshot = {
  width: number;
  height: number;
  indices: Uint8Array;
  palette: Uint8ClampedArray;
};

/**
 * A compact snapshot of the region-related state fields. Stored on the undo
 * stack before each destructive region operation.
 *
 * `assetData` is intentionally excluded: region ops never mutate binary pixel
 * data - only the labelPlane, entries, and metadata change. Asset uploads go
 * through a separate path and are not undoable via this stack.
 *
 * Critical-2 fix: `labelMap` and `regions` are included so that undo can
 * restore the full render-pipeline state, not just the region-panel UI.
 * Without these fields, Cmd/Ctrl+Z restores the panel but leaves the
 * dithered output stale.
 *
 * Mutable buffers are deep-copied on push (see pushUndoSnapshot) because
 * later edits must not corrupt the snapshot. RegionEntry / RegionConfig
 * arrays are structurally immutable across ops so shallow-copying them is safe.
 */
export type RegionUndoSnapshot = {
  regionEntries: RegionEntry[];
  regionStacks: RegionStack[];
  assetLibrary: Asset[];
  regionLabelPlane: Uint8Array;
  activeRegionUuids: string[];
  /** Active stack UUID at snapshot time - Item 32 (v0.2.4.3). */
  activeStackUuid: string | null;
  /** Indexed labelMap at snapshot time. null when no labelmap was loaded. */
  labelMap: IndexedLabelMapSnapshot | null;
  /** RegionConfig[] derived from regionEntries at snapshot time. */
  regions: RegionConfig[];
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum number of snapshots retained. Oldest is dropped when exceeded. */
export const UNDO_STACK_MAX = 3;

// ---------------------------------------------------------------------------
// labelMap packing
// ---------------------------------------------------------------------------

function imageDataToIndexed(src: ImageData): IndexedLabelMapSnapshot {
  const total = src.width * src.height;
  const indices = new Uint8Array(total);
  let maxLabel = 0;

  for (let i = 0; i < total; i++) {
    const label = src.data[i * 4]!;
    indices[i] = label;
    if (label > maxLabel) maxLabel = label;
  }

  const palette = new Uint8ClampedArray((maxLabel + 1) * 4);
  const seen = new Uint8Array(maxLabel + 1);
  for (let i = 0; i < total; i++) {
    const label = indices[i]!;
    if (seen[label] === 1) continue;
    seen[label] = 1;
    const srcOffset = i * 4;
    const paletteOffset = label * 4;
    palette[paletteOffset] = src.data[srcOffset]!;
    palette[paletteOffset + 1] = src.data[srcOffset + 1]!;
    palette[paletteOffset + 2] = src.data[srcOffset + 2]!;
    palette[paletteOffset + 3] = src.data[srcOffset + 3]!;
  }

  return { width: src.width, height: src.height, indices, palette };
}

function indexedToImageData(src: IndexedLabelMapSnapshot): ImageData {
  const out = new ImageData(src.width, src.height);
  for (let i = 0; i < src.indices.length; i++) {
    const label = src.indices[i]!;
    const outOffset = i * 4;
    const paletteOffset = label * 4;
    if (paletteOffset + 3 < src.palette.length) {
      out.data[outOffset] = src.palette[paletteOffset]!;
      out.data[outOffset + 1] = src.palette[paletteOffset + 1]!;
      out.data[outOffset + 2] = src.palette[paletteOffset + 2]!;
      out.data[outOffset + 3] = src.palette[paletteOffset + 3]!;
    } else {
      out.data[outOffset] = label;
      out.data[outOffset + 1] = 0;
      out.data[outOffset + 2] = 0;
      out.data[outOffset + 3] = 255;
    }
  }
  return out;
}

export function restoreSnapshotLabelMap(
  labelMap: IndexedLabelMapSnapshot | null,
): ImageData | null {
  return labelMap === null ? null : indexedToImageData(labelMap);
}

// ---------------------------------------------------------------------------
// Push - called before each destructive op
// ---------------------------------------------------------------------------

/**
 * Snapshot the current region state and return a new undo stack with the
 * snapshot appended. If the stack would exceed UNDO_STACK_MAX, the oldest
 * entry is dropped (FIFO).
 *
 * The regionLabelPlane is deep-copied (new Uint8Array) because it is a
 * mutable buffer; all other snapshot fields are immutable (RegionEntry /
 * RegionStack objects are never mutated in-place - region ops replace the
 * entire array). Shallow-copying those arrays is therefore safe.
 *
 * @param state - Current DemoState before the op is applied.
 * @returns The updated undo stack to be stored in state.regionUndoStack.
 */
export function pushUndoSnapshot(state: DemoState): RegionUndoSnapshot[] {
  const snapshot: RegionUndoSnapshot = {
    regionEntries: state.regionEntries.slice(),
    regionStacks: state.regionStacks.slice(),
    assetLibrary: state.assetLibrary.slice(),
    // CRITICAL: Uint8Array is mutable - must deep-copy so later writes to
    // the live plane do not corrupt the snapshot.
    regionLabelPlane: new Uint8Array(state.regionLabelPlane),
    activeRegionUuids: state.activeRegionUuids.slice(),
    // Item 32: capture active stack selection so undo restores it correctly.
    activeStackUuid: state.activeStackUuid,
    // Critical-2 fix: snapshot render-pipeline fields so undo can fully
    // restore the dither output, not just the panel UI.
    labelMap:
      state.labelMap !== null ? imageDataToIndexed(state.labelMap) : null,
    regions: state.regions.slice(),
  };

  const next = [...state.regionUndoStack, snapshot];
  if (next.length > UNDO_STACK_MAX) {
    // Drop the oldest snapshot (index 0) when the cap is exceeded.
    next.shift();
  }
  return next;
}

// ---------------------------------------------------------------------------
// Pop - called by the Cmd/Ctrl+Z handler
// ---------------------------------------------------------------------------

/**
 * Remove and return the top snapshot from the undo stack.
 *
 * @param state - Current DemoState.
 * @returns An object with the popped snapshot (or null when the stack is
 *   empty) and the updated stack to store back into state.
 */
export function popUndoSnapshot(state: DemoState): {
  snapshot: RegionUndoSnapshot | null;
  nextStack: RegionUndoSnapshot[];
} {
  if (state.regionUndoStack.length === 0) {
    return { snapshot: null, nextStack: state.regionUndoStack };
  }
  const next = state.regionUndoStack.slice();
  const snapshot = next.pop()!;
  return { snapshot, nextStack: next };
}
